import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-4 mt-4">
      <div className="d-flex align-items-center gap-3">
        <span className="text-gray-700 fs-7 fw-semibold">Itens por página:</span>
        <select
          className="form-select form-select-sm form-select-solid"
          style={{ width: 'auto', minWidth: '80px' }}
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-gray-600 fs-7">
          Mostrando {startItem} a {endItem} de {totalItems} registros
        </span>
      </div>

      {totalPages > 1 && (
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm btn-light-primary"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            title="Primeira página"
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            className="btn btn-sm btn-light-primary"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            title="Página anterior"
          >
            <ChevronLeft size={16} />
          </button>

          <div className="d-flex gap-1">
            {getPageNumbers().map((page, index) =>
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-600">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  className={`btn btn-sm ${
                    currentPage === page
                      ? 'btn-primary'
                      : 'btn-light-primary'
                  }`}
                  onClick={() => onPageChange(page as number)}
                  style={{ minWidth: '36px' }}
                >
                  {page}
                </button>
              )
            )}
          </div>

          <button
            className="btn btn-sm btn-light-primary"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Próxima página"
          >
            <ChevronRight size={16} />
          </button>
          <button
            className="btn btn-sm btn-light-primary"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            title="Última página"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
